import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

export type Edital = {
  id: string;
  titulo: string;
  modalidade: string;
  inscricoes: number;
  status: string;
  dataFim: string;
};

export default function EditaisPanel({ editais }: { editais: Edital[] }) {
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");

  // Filtrar editais
  const editaisFiltrados = editais.filter((edital) => {
    const matchBusca =
      edital.titulo.toLowerCase().includes(busca.toLowerCase()) ||
      edital.id.toLowerCase().includes(busca.toLowerCase());

    const matchStatus =
      filtroStatus === "todos" || edital.status === filtroStatus;

    return matchBusca && matchStatus;
  });

  // Limitar a 6 editais para o dashboard
  const editaisLimitados = editaisFiltrados.slice(0, 6);

  return (
    <div className="bg-white border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-900">Editais</h2>
          <span className="text-sm text-gray-500">
            {editaisLimitados.length} de {editais.length}
          </span>
        </div>
        <button
          onClick={() => navigate("/admin/editais")}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
        >
          Ver todos
        </button>
      </div>
      <div className="p-6">
        <div className="mb-4 flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por título ou ID..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          >
            <option value="todos">Todos os status</option>
            <option value="Aberto">Aberto</option>
            <option value="Rascunho">Rascunho</option>
            <option value="Encerrado">Encerrado</option>
          </select>
        </div>

        {/* Container com altura fixa para manter layout estável */}
        <div className="min-h-[400px]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  ID
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  Título
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  Modalidade
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  Inscrições
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  Status
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3">
                  Prazo
                </th>
                <th className="text-left text-xs font-medium text-gray-600 pb-3"></th>
              </tr>
            </thead>
            <tbody>
              {editaisLimitados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    {busca || filtroStatus !== "todos"
                      ? "Nenhum edital encontrado com os filtros aplicados"
                      : "Nenhum edital cadastrado"}
                  </td>
                </tr>
              ) : (
                editaisLimitados.map((edital) => (
                  <tr
                    key={edital.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 text-sm text-gray-600">{edital.id}</td>
                    <td className="py-3 text-sm text-gray-900">
                      {edital.titulo}
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {edital.modalidade}
                    </td>
                    <td className="py-3 text-sm text-gray-900">
                      {edital.inscricoes}
                    </td>
                    <td className="py-3">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          edital.status === "Aberto"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : edital.status === "Encerrado"
                            ? "bg-gray-50 text-gray-700 border border-gray-200"
                            : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                        }`}
                      >
                        {edital.status}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {new Date(edital.dataFim).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => navigate(`/admin/editais/${edital.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline transition-colors"
                      >
                        Gerenciar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {editaisFiltrados.length > 6 && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/admin/editais")}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
              >
                + {editaisFiltrados.length - 6} editais não exibidos. Clique
                para ver todos
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
